import axios, {
  AxiosInstance,
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { getGlobalAccessToken } from '../utils/authUtils';

// Global toast function - will be set by the app
let globalShowToast: ((message: string, severity?: 'error' | 'warning' | 'info' | 'success') => void) | null = null;

export const setGlobalToast = (toastFn: (message: string, severity?: 'error' | 'warning' | 'info' | 'success') => void) => {
  globalShowToast = toastFn;
};

class ApiClient {
  private readonly apiClient: AxiosInstance;

  constructor(baseURL: string) {
    this.apiClient = axios.create({
      baseURL: baseURL,
      headers: {
        'Content-Type': 'application/json'
      },
      withCredentials: true
    });

    this.apiClient.interceptors.request.use(this.addAuthHeader);
    this.apiClient.interceptors.response.use(
      (response) => response,
      (error) => this.handleTokenExpiration(error)
    );
    this.apiClient.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 0 && error.message?.includes('CORS')) {
          console.error('CORS Error:', error);
          // Handle CORS error - could redirect to error page or retry
        }
        return Promise.reject(error);
      }
    );
  }

  // Add Authorization header to every request
  private readonly addAuthHeader = (
    config: InternalAxiosRequestConfig
  ): InternalAxiosRequestConfig => {
    let accessToken = null;
    
    // First try to get token from global auth getter (preferred method)
    accessToken = getGlobalAccessToken();
    
    // If global getter didn't work, try localStorage methods
    if (!accessToken) {
      try {
        // react-oidc-context stores user data under 'oidc.user' key
        const oidcUser = localStorage.getItem('oidc.user');
        if (oidcUser) {
          const userData = JSON.parse(oidcUser);
          accessToken = userData.access_token;
        }
        
        // If not found in oidc.user, try other possible keys
        if (!accessToken) {
          // Try the key pattern that react-oidc-context might use
          const keys = Object.keys(localStorage);
          for (const key of keys) {
            if (key.includes('oidc.user') || key.includes('user')) {
              try {
                const data = JSON.parse(localStorage.getItem(key) || '{}');
                if (data.access_token) {
                  accessToken = data.access_token;
                  break;
                }
              } catch (e) {
                // Continue to next key
              }
            }
          }
        }
        
        // Fallback to manual token storage
        if (!accessToken) {
          accessToken = localStorage.getItem("access_token");
        }
      } catch (error) {
        console.error('Error getting access token:', error);
        // Final fallback
        accessToken = localStorage.getItem("access_token");
      }
    }
    
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
      // Token added successfully - no need to log every request
    } else {
      console.warn('❌ No access token found for API request');
    }
    
    return config;
  };

  // Handle token expiration and authentication errors
  private readonly handleTokenExpiration = async (
    error: AxiosError
  ): Promise<AxiosResponse | void> => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Check for any authentication errors (401, 403) or specific error messages
    const isAuthError = error.response && 
      (error.response.status === 401 || 
       error.response.status === 403 || 
       (error.response.data as any)?.errorCode === 'TOKEN_EXPIRED' ||
       (error.response.data as any)?.detail === 'Token has expired');

    if (isAuthError && !originalRequest._retry) {
      // Mark as retried to prevent infinite loops
      originalRequest._retry = true;
      
      // Log the authentication error but DO NOT redirect
      console.warn('Authentication error (401/403) - user needs to sign in');
      
      // Show user-friendly notification without redirect
      if (globalShowToast) {
        globalShowToast("Authentication required. Please sign in.", "warning");
      }
      
      // Just reject the promise - let the app handle navigation
      return Promise.reject(new Error("Authentication required"));
    }

    // Handle 401 errors that already retried - just reject without additional toast
    if (error.response?.status === 401 && originalRequest._retry) {
      return Promise.reject(new Error("Authentication failed"));
    }

    return Promise.reject(new Error(error?.message || "Request failed"));
  };

  // Expose apiClient instance
  public getClient(): AxiosInstance {
    return this.apiClient;
  }
}

export default ApiClient;
